import { styled } from 'baseui'
import Icons from '../Icons'
import React, { useEffect, useState, useRef } from 'react';
import HeadingInspector from '@/components/HeadingInspector'
import AutoScroll from '@/components/AutoScroll'
import { PersistentPanelData, useInspector } from './InspectorDataProvider';
import FooterActionButtons, { HasFooterActionButtons, SingleButtonVariant } from './ToolboxItems/components/FooterActionButtons'
import { SizeButton } from '@/constants/sizeButton'
import { useTranslation } from 'react-i18next';
import { LayoutUtils } from '@/utils/LayoutUtils';
import { useSelector } from 'react-redux';
import { selectDisableEditorState } from '@/store/slices/editor/selector';
import { Z_INDEX } from '@/constants/z-index';

const InspectorContainer = styled('div', props => ({
  backgroundColor: props.$theme.colors.background,
  boxShadow: '0px 0px 6px 0px rgba(0, 0, 0, 0.08)',
  display: 'flex',
  width: '340px',
  maxWidth: '100%',
  height: '100%',
  boxSizing: 'border-box',
  borderRadius: '12px',
  overflow: 'hidden',
  zIndex: Z_INDEX.INSPECTOR_PANEL,
  alignItems: 'flex_start',
  flexDirection: 'column'
}))

interface InspectorPanelProps {
  title: string;
  hasNavigation?: boolean;
  hasClose?: boolean;
  hasAutoScroll?: boolean;
  returnToLastScrollPosition?: boolean;
  hasFooterActionButtons?: HasFooterActionButtons;
  onClickPrimary?:()=>void,
  onClickSecondary?:()=>void,
  isPrimaryButtonActive?: boolean,
  isSecondaryButtonActive?: boolean,
  primaryButtonText?: string,
  hasQuestionMarkButton?: boolean,
  questionMarkButtonAction?: ()=>void,
  onClickSecondaryShowDiscardModal?: boolean
  disablePanelOnPrimaryPress?: boolean
  updatePersistedData?: (data: PersistentPanelData) => void;
  persistedData?: PersistentPanelData;
  handleNavigation?: () => void;
  showDiscardOnNavigation?: boolean;
  hasAddMenu?: boolean;
  disableNavigation?: boolean;
  showFooterWithAnimation?: boolean;
  hideFooterWithAnimation?: boolean;
  /**
   * When true the panel renders as a plain inline card (no editor-canvas
   * positioning, no InspectorProvider dependency). Use this mode when
   * rendering outside the editor (e.g. Batch Studio).
   * Pair with `onClose` for the close/navigation callback.
   */
  standalone?: boolean;
  onClose?: () => void;
  /**
   * Standalone only — handler for the header's back arrow, so a pushed sub-panel
   * can pop instead of closing the whole panel. Defaults to `onClose`.
   */
  onBack?: () => void;
  /** Shows a Reset button in the header instead of one inline in the panel content. */
  hasReset?: boolean;
  onReset?: () => void;
  isResetActive?: boolean;
  /** Colour treatment for the `singleButton` footer. Defaults to the black "Apply all" button. */
  singleButtonVariant?: SingleButtonVariant;
  /** Lets a black `singleButton` footer grey out when `isPrimaryButtonActive` is false. */
  honourDisabledOnBlack?: boolean;
  /** Rendered above the `singleButton` footer's primary button. */
  footerAboveButton?: React.ReactNode;
  /** Height of the `singleButton` footer's primary. Defaults to LARGE (48px). */
  singleButtonSize?: SizeButton;
  /** Optional line rendered under the footer's single button. */
  footerCaption?: string;
  /** Overrides the Reset button's label (e.g. 'Clear'). Defaults to 'Reset'. */
  resetLabel?: string;
}

/**
 * Lightweight version of InspectorPanel used when rendering outside the
 * editor canvas (no InspectorProvider, no absolute positioning).
 * Exposes the same white-card chrome (title, close button, scrollable body)
 * as the editor flavour.
 */
const InspectorPanelStandalone: React.FC<Pick<
  InspectorPanelProps,
  'title' | 'hasNavigation' | 'hasClose' | 'hasAutoScroll' | 'onClose' | 'onBack' | 'hasReset' | 'onReset' | 'isResetActive'
  | 'hasFooterActionButtons' | 'onClickPrimary' | 'onClickSecondary' | 'isPrimaryButtonActive' | 'isSecondaryButtonActive'
  | 'primaryButtonText' | 'onClickSecondaryShowDiscardModal' | 'showFooterWithAnimation' | 'hideFooterWithAnimation'
  | 'singleButtonVariant' | 'honourDisabledOnBlack' | 'footerCaption' | 'footerAboveButton' | 'singleButtonSize' |
  'resetLabel'
> & { children?: React.ReactNode }> = ({
  title,
  hasNavigation = false,
  hasClose = false,
  hasAutoScroll = true,
  onClose = () => {},
  onBack,
  hasReset,
  onReset,
  isResetActive,
  hasFooterActionButtons,
  onClickPrimary = () => {},
  onClickSecondary = () => {},
  isPrimaryButtonActive = false,
  isSecondaryButtonActive = true,
  primaryButtonText,
  onClickSecondaryShowDiscardModal = true,
  showFooterWithAnimation = false,
  hideFooterWithAnimation = false,
  singleButtonVariant,
  honourDisabledOnBlack,
  footerCaption,
  footerAboveButton,
  singleButtonSize,
  resetLabel,
  children,
}) => {
  const [isBoxShadow, setIsBoxShadow] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  return (
    // Mirrors the editor InspectorPanel's top:16px / bottom:16px positioning.
    // position:absolute fills the AnimatedInspectorWrapper parent (which is
    // already position:absolute/100%×100%). pointerEvents:auto overrides the
    // wrapper's pointerEvents:none so the panel is interactive.
    <div
      style={{
        pointerEvents: 'auto',
        position: 'absolute',
        top: '16px',
        bottom: '16px',
        left: 0,
        right: 0,
      }}
    >
      <InspectorContainer data-inspector-panel="">
        <HeadingInspector
          hasBoxShadow={isBoxShadow}
          hasNavigation={hasNavigation}
          hasClose={hasClose}
          handleNavigation={onBack ?? onClose}
          handleClose={onClose}
          title={title}
          hasReset={hasReset}
          onReset={onReset}
          isResetActive={isResetActive}
          resetLabel={resetLabel}
        />
        {hasAutoScroll ? (
          <AutoScroll
            ref={scrollContainerRef}
            handleScroll={() => {
              setIsBoxShadow((scrollContainerRef.current?.scrollTop ?? 0) > 0)
            }}
            style={{ marginTop: 0 }}
            bottomMarginToContent
          >
            {children}
          </AutoScroll>
        ) : (
          <>{children}</>
        )}
        {hasFooterActionButtons && hasFooterActionButtons !== 'none' && (
          <FooterActionButtons
            hasFooterActionButtons={hasFooterActionButtons}
            onClickPrimary={onClickPrimary}
            onClickSecondary={onClickSecondary}
            primaryButtonText={primaryButtonText}
            isPrimaryButtonActive={isPrimaryButtonActive}
            isSecondaryButtonActive={isSecondaryButtonActive}
            onClickSecondaryShowDiscardModal={onClickSecondaryShowDiscardModal}
            showFooterWithAnimation={showFooterWithAnimation}
            hideFooterWithAnimation={hideFooterWithAnimation}
            singleButtonVariant={singleButtonVariant}
            honourDisabledOnBlack={honourDisabledOnBlack}
            caption={footerCaption}
            aboveButton={footerAboveButton}
            singleButtonSize={singleButtonSize}
          />
        )}
      </InspectorContainer>
    </div>
  )
}

const InspectorPanelEditor: React.FC<InspectorPanelProps> = ({ 
  title, 
  hasNavigation = false, 
  hasClose = false, 
  hasAutoScroll = true,
  returnToLastScrollPosition = false,
  hasFooterActionButtons,
  onClickPrimary=()=>{},
  onClickSecondary=()=>{},
  isPrimaryButtonActive = false,
  isSecondaryButtonActive = true,
  primaryButtonText,
  hasQuestionMarkButton = false,
  questionMarkButtonAction=()=>{},
  onClickSecondaryShowDiscardModal = true,
  disablePanelOnPrimaryPress = true,
  updatePersistedData,
  persistedData,
  handleNavigation,
  showDiscardOnNavigation,
  disableNavigation = false,
  showFooterWithAnimation = false,
  hideFooterWithAnimation = false,
  hasReset = false,
  onReset,
  isResetActive = false,
  resetLabel,
  children }) => {
  const [isBoxShadow, setIsBoxShadow] = useState(false);
  const { hideInspector } = useInspector();
  const [isScrollbarVisible, setIsScrollbarVisible] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const disableEditorState = useSelector(selectDisableEditorState)
  const hasAddMenu = disableEditorState.addMenu !== 'hidden'
  const [isDisabledLocal, setIsDisabledLocal] = useState(false)
  const [isAfterSecondaryClick, setIsAfterSecondaryClick] = useState(false)

  const [t] = useTranslation()
  const discardModalHeader = t('Discard changes?')
  const discardModalDescription = t('Your changes will not be saved.')  

  useEffect(() => {
    const targetNode = scrollContainerRef.current;
    if (!targetNode) return;

    const checkScrollbarClass = () => {
      const hasScrollbarClass = targetNode.classList.contains('scrollbar-visible');
      setIsScrollbarVisible(hasScrollbarClass);
    };

    // Create a MutationObserver to watch for changes in the class attribute
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.attributeName === 'class') {
          checkScrollbarClass();
        }
      }
    });

    observer.observe(targetNode, { attributes: true });
    checkScrollbarClass();

    return () => {
      observer.disconnect();
    };
  }, []);

  return <div style={{
    pointerEvents: 'auto',
    left: hasAddMenu ? `${LayoutUtils.ADD_MENU_MARGIN_LEFT + LayoutUtils.ADD_MENU_WIDTH + LayoutUtils.ADD_MENU_INSPECTOR_GAP}px` : `${LayoutUtils.ADD_MENU_MARGIN_LEFT}px`,
    position: 'absolute',
    top: '16px',
    bottom: '16px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    transition: '0.4s ease-in-out'
  }
}>
  <div style={{ position: 'relative', width: '100%', height: '100%' }}>
    {isDisabledLocal && (
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: '12px',
        zIndex: Z_INDEX.INSPECTOR_PANEL + 1,
        pointerEvents: 'auto',
        cursor: 'not-allowed',
        backgroundColor: 'rgba(255, 255, 255, 0.0)',
      }} />
    )}
    <InspectorContainer data-inspector-panel="">{
    <>
    <HeadingInspector
      hasBoxShadow={isBoxShadow}
      hasNavigation={hasNavigation}
      hasClose={hasClose}
      handleNavigation={()=>{ handleNavigation ? handleNavigation() : hideInspector()}}
      handleClose={()=>{ hideInspector(); }}
      title={title}
      hasQuestionMarkButton={hasQuestionMarkButton}
      questionMarkButtonAction={questionMarkButtonAction}
      showDiscardModalOnChanges = {showDiscardOnNavigation}
      discardModalHeader={discardModalHeader}
      discardModalDescription={discardModalDescription}
      areChangesToDiscard={isPrimaryButtonActive}
      disableNavigation={disableNavigation}
      hasReset={hasReset}
      onReset={onReset}
      isResetActive={isResetActive}
      resetLabel={resetLabel}
    ></HeadingInspector>
    { hasAutoScroll ? ( 
        <AutoScroll
          ref={scrollContainerRef}
          initialScrollTop={returnToLastScrollPosition ? persistedData?.scrollTop : undefined}
          handleScroll={e => {
            if (scrollContainerRef.current.scrollTop > 0) {
              setIsBoxShadow(true)
            } else {
              setIsBoxShadow(false)
            }
            if (returnToLastScrollPosition) {
              updatePersistedData?.({scrollTop: scrollContainerRef.current.scrollTop})
            }
          }}
          style={{ marginTop: 0 }}
          bottomMarginToContent={ true }
        >
          {children}
        </AutoScroll>) :
        (<>{children}</>)
    }
    { hasFooterActionButtons && hasFooterActionButtons !== 'none' &&
      <FooterActionButtons
        hasFooterActionButtons={hasFooterActionButtons}
        onClickPrimary={() => {
          if (disablePanelOnPrimaryPress) setIsDisabledLocal(true);
          onClickPrimary()}
        }
        onClickSecondary={() => {
          setIsDisabledLocal(true);
          setIsAfterSecondaryClick(true);
          onClickSecondary()}
        }
        primaryButtonText={primaryButtonText}
        isPrimaryButtonActive={isPrimaryButtonActive && !isAfterSecondaryClick}
        isSecondaryButtonActive={isSecondaryButtonActive}
        onClickSecondaryShowDiscardModal={onClickSecondaryShowDiscardModal}
        discardModalHeader={discardModalHeader}
        discardModalDescription={discardModalDescription}
        showFooterWithAnimation={showFooterWithAnimation}
        hideFooterWithAnimation={hideFooterWithAnimation}
      >
      </FooterActionButtons> }
    </>
    }</InspectorContainer>
   </div>
  </div>
}

/**
 * Inspector panel chrome used across the editor and in standalone contexts
 * (e.g. Batch Studio).  Pass `standalone` to suppress editor-specific
 * positioning/hooks; use `onClose` for the close/navigate callback in that
 * mode.
 */
const InspectorPanel: React.FC<InspectorPanelProps> = ({ standalone, onClose, ...rest }) => {
  if (standalone) {
    return (
      <InspectorPanelStandalone
        title={rest.title}
        hasNavigation={rest.hasNavigation}
        hasClose={rest.hasClose}
        hasAutoScroll={rest.hasAutoScroll}
        onClose={onClose}
        onBack={rest.onBack}
        hasReset={rest.hasReset}
        onReset={rest.onReset}
        isResetActive={rest.isResetActive}
        hasFooterActionButtons={rest.hasFooterActionButtons}
        onClickPrimary={rest.onClickPrimary}
        onClickSecondary={rest.onClickSecondary}
        isPrimaryButtonActive={rest.isPrimaryButtonActive}
        isSecondaryButtonActive={rest.isSecondaryButtonActive}
        primaryButtonText={rest.primaryButtonText}
        onClickSecondaryShowDiscardModal={rest.onClickSecondaryShowDiscardModal}
        showFooterWithAnimation={rest.showFooterWithAnimation}
        hideFooterWithAnimation={rest.hideFooterWithAnimation}
        singleButtonVariant={rest.singleButtonVariant}
        honourDisabledOnBlack={rest.honourDisabledOnBlack}
        footerCaption={rest.footerCaption}
        footerAboveButton={rest.footerAboveButton}
        singleButtonSize={rest.singleButtonSize}
        resetLabel={rest.resetLabel}
      >
        {rest.children}
      </InspectorPanelStandalone>
    )
  }
  return <InspectorPanelEditor {...rest} onClose={onClose} />
}

export default InspectorPanel
